import { createContext, useContext, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [profile, setProfile] = useState(null); // {id, title}
  const [items, setItems] = useState([]); // [{service_id, service_name, price_min, price_max, unit, quantity}]

  const setActiveProfile = useCallback((p) => {
    setProfile((prev) => {
      if (prev && prev.id !== p.id) setItems([]);
      return { id: p.id, title: p.title };
    });
  }, []);

  const addItem = useCallback((svc) => {
    setItems((prev) => {
      const ex = prev.find((i) => i.service_id === svc.id);
      if (ex) return prev.map((i) => (i.service_id === svc.id ? { ...i, quantity: i.quantity + 1 } : i));
      return [
        ...prev,
        {
          service_id: svc.id,
          service_name: svc.name,
          price_min: svc.min,
          price_max: svc.max,
          unit: svc.unit || null,
          quantity: 1,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.service_id === id ? { ...i, quantity: Math.max(1, qty) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.service_id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const min = items.reduce((s, i) => s + i.price_min * i.quantity, 0);
    const max = items.reduce((s, i) => s + i.price_max * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { min, max, count };
  }, [items]);

  return (
    <CartContext.Provider value={{ profile, setActiveProfile, items, addItem, updateQty, removeItem, clear, totals }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};
